import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, CalendarClock, Plane, Wallet, Briefcase, Target, UserCircle, LogOut, Menu, Settings as SettingsIcon } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { Wordmark } from "./Wordmark";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, key: "dashboard" },
  { to: "/employees", label: "Employees", empLabel: "Directory", icon: Users, key: "employees" },
  { to: "/attendance", label: "Attendance", icon: CalendarClock, key: "attendance" },
  { to: "/leaves", label: "Leaves", icon: Plane, key: "leaves" },
  { to: "/payroll", label: "Payroll", empLabel: "Payslips", icon: Wallet, key: "payroll" },
  { to: "/recruitment", label: "Recruitment", icon: Briefcase, key: "recruitment", admin: true },
  { to: "/performance", label: "Performance", icon: Target, key: "performance" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, key: "settings", admin: true },
  { to: "/profile", label: "My Profile", icon: UserCircle, key: "profile" },
];

const SidebarBody = ({ onNav }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin";
  return (
    <div className="flex flex-col h-full justify-between">
      <div>
        <div className="px-3 pt-2 pb-8">
          <Wordmark dark className="text-2xl" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-1">People OS</div>
        </div>
        <nav className="space-y-1">
          {NAV.filter((n) => !n.admin || isAdmin).map((n) => (
            <NavLink key={n.key} to={n.to} end={n.to === "/"} onClick={onNav} data-testid={`nav-${n.key}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-[#E08A1E] text-white font-semibold" : "text-neutral-400 hover:text-white hover:bg-neutral-800/60 font-medium"}`}>
              <n.icon className="h-4 w-4" />
              {!isAdmin && n.empLabel ? n.empLabel : n.label}
            </NavLink>
          ))}
        </nav>
      </div>
      <div className="border-t border-[#2C2A29] pt-4">
        <div className="px-3 mb-3">
          <div className="text-sm font-semibold text-white truncate" data-testid="sidebar-user-name">{user.name}</div>
          <div className="text-xs text-neutral-500 truncate">{isAdmin ? "HR / Admin" : user.designation || "Employee"}</div>
        </div>
        <button data-testid="logout-button" onClick={async () => { await logout(); navigate("/login"); }}
          className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-neutral-400 hover:text-white hover:bg-neutral-800/60 transition-colors">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </div>
  );
};

export default function Layout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <aside className="hidden lg:block fixed left-0 top-0 h-screen w-64 bg-[#0A0807] border-r border-[#2C2A29] p-4 z-30">
        <SidebarBody />
      </aside>
      <header className="lg:hidden sticky top-0 z-40 backdrop-blur-md bg-[#F5F5F7]/85 border-b border-[#E5E5EA] px-4 py-3 flex items-center justify-between">
        <Wordmark className="text-xl" />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-button" className="p-2 rounded-lg hover:bg-white"><Menu className="h-5 w-5" /></button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 bg-[#0A0807] border-[#2C2A29] p-4">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SidebarBody onNav={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </header>
      <main className="lg:ml-64 p-4 sm:p-6 md:p-10 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
